import React from "react";
import { Link } from "react-router-dom";
import {
  Target,
  Eye,
  Heart,
  Users,
  ShieldCheck,
  Building2,
  ArrowRight,
  MapPin,
  BookOpen,
  Briefcase,
  Home as HomeIcon,
  Sparkles,
  Compass,
  Layers,
  Search,
  SlidersHorizontal,
  MessageCircle,
  TrendingUp,
  ListChecks,
  BellRing,
  Lightbulb,
  CheckCircle2,
  KeyRound,
  HandshakeIcon,
} from "lucide-react";

const stats = [
  { value: "25K+", label: "Beds Listed", Icon: Building2 },
  { value: "5K+", label: "Verified Properties", Icon: ShieldCheck },
  { value: "50+", label: "Cities Covered", Icon: MapPin },
  { value: "100K+", label: "Tenants Connected", Icon: Users },
];

const beliefs = [
  "Finding a home should be simple, not stressful.",
  "Trust and transparency are non-negotiable.",
  "Technology should serve people — not the other way around.",
  "Every tenant deserves clarity. Every owner deserves reach.",
];

const tenantFeatures = [
  {
    Icon: ShieldCheck,
    title: "Verified Listings",
    desc: "Every property goes through our verification checks so you only see real, reliable options.",
  },
  {
    Icon: SlidersHorizontal,
    title: "Smart Search & Filtering",
    desc: "Filter by budget, occupancy, amenities, food preferences and more to find exactly what fits you.",
  },
  {
    Icon: MapPin,
    title: "Location-Based Discovery",
    desc: "Find PGs near your college, workplace, or favourite metro station with map-first browsing.",
  },
  {
    Icon: MessageCircle,
    title: "Direct Owner Connect",
    desc: "No brokers, no middlemen. Talk directly to property owners and close faster.",
  },
];

const ownerFeatures = [
  {
    Icon: ListChecks,
    title: "Easy Property Listing",
    desc: "List your property in minutes with a guided flow built for owners, not tech experts.",
  },
  {
    Icon: TrendingUp,
    title: "Enhanced Visibility",
    desc: "Get discovered by thousands of active tenants searching in your locality every day.",
  },
  {
    Icon: BellRing,
    title: "Quality Lead Generation",
    desc: "Receive genuine, intent-driven enquiries from tenants who are ready to move in.",
  },
  {
    Icon: HandshakeIcon,
    title: "Inquiry Management",
    desc: "Track, respond and convert leads from a single, simple owner dashboard.",
  },
];

const values = [
  {
    Icon: ShieldCheck,
    title: "Verified Listings",
    desc: "Trust is the foundation of every successful rental experience. We focus on reliable property information so you always know what to expect.",
  },
  {
    Icon: Eye,
    title: "Transparent Information",
    desc: "Clear details on properties, amenities, locations and pricing ensure you can make fully informed decisions — no hidden surprises.",
  },
  {
    Icon: Heart,
    title: "User-Centric Design",
    desc: "Our platform is built to be intuitive and efficient — helping users find the right accommodation without unnecessary steps.",
  },
  {
    Icon: Compass,
    title: "Local Expertise",
    desc: "We understand the unique needs of students, professionals and owners across different cities and neighbourhoods.",
  },
  {
    Icon: Lightbulb,
    title: "Continuous Innovation",
    desc: "We constantly improve our platform, tools and processes to deliver a smarter rental experience for everyone.",
  },
];

const audience = [
  {
    Icon: BookOpen,
    title: "Students",
    desc: "Find safe, affordable PG accommodation near your college or university. Focus on your studies, not your housing search.",
  },
  {
    Icon: Briefcase,
    title: "Working Professionals",
    desc: "Discover convenient living spaces close to your workplace, business district, or preferred transit hub in India's fast-growing urban centres.",
  },
  {
    Icon: HomeIcon,
    title: "Property Owners",
    desc: "Reach quality tenants, reduce vacancy periods and grow your rental income with smart digital tools designed specifically for PG operators.",
  },
  {
    Icon: Layers,
    title: "Co-Living Operators",
    desc: "Showcase modern, flexible living spaces to a growing community of urban renters seeking community, convenience and managed experiences.",
  },
];

const journey = [
  {
    Icon: Search,
    step: "01",
    title: "Discover",
    desc: "Search verified PGs by location, budget and lifestyle preferences.",
  },
  {
    Icon: MessageCircle,
    step: "02",
    title: "Connect",
    desc: "Talk directly to property owners — ask questions, schedule visits.",
  },
  {
    Icon: KeyRound,
    step: "03",
    title: "Move In",
    desc: "Pick your match, finalise the deal and start your new chapter.",
  },
];

export default function About() {
  return (
    <main className="abt-root">
      {/* Hero */}
      <section className="abt-hero">
        <div className="abt-container">
          <span className="abt-eyebrow">
            <span className="abt-dot" /> Our Story
          </span>
          <h1 className="abt-h1">
            We're making PG hunting <span className="abt-accent">human again</span>
          </h1>
          <p className="abt-lead">
            AapkaPG connects students, working professionals, and property owners on
            India's trusted platform for verified PG accommodations and co-living
            spaces — making the journey from search to stay simpler, faster, and
            stress-free.
          </p>
          <p className="abt-attribution">
            AapkaPG.com is a product of <strong>Fast Management Service Private Limited.</strong>
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="abt-stats-wrap">
        <div className="abt-container">
          <div className="abt-stats">
            {stats.map((s) => (
              <div key={s.label} className="abt-stat">
                <div className="abt-stat-icon">
                  <s.Icon />
                </div>
                <div className="abt-stat-value">{s.value}</div>
                <div className="abt-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission, Vision & What We Believe */}
      <section className="abt-section">
        <div className="abt-container">
          <div className="abt-mv-grid">
            <div className="abt-card abt-card-pad">
              <div className="abt-card-icon">
                <Target />
              </div>
              <h2 className="abt-h2">Our Mission</h2>
              <p className="abt-p">
                To simplify the accommodation search process through technology,
                trusted listings, and user-focused experiences that help people find
                the right place to live with confidence — and help property owners
                run thriving, well-connected rental businesses.
              </p>
            </div>
            <div className="abt-card abt-card-pad">
              <div className="abt-card-icon">
                <Eye />
              </div>
              <h2 className="abt-h2">Our Vision</h2>
              <p className="abt-p">
                To become India's most trusted and preferred platform for PG
                accommodation discovery and management — creating meaningful
                connections between tenants and property owners while making shared
                living more accessible, transparent and convenient.
              </p>
            </div>
          </div>

          <div className="abt-believe">
            <div className="abt-believe-head">
              <span className="abt-eyebrow abt-eyebrow-light">What we believe</span>
              <h2 className="abt-h2">Principles that guide every decision</h2>
            </div>
            <ul className="abt-believe-list">
              {beliefs.map((b) => (
                <li key={b}>
                  <CheckCircle2 className="abt-believe-check" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="abt-section abt-cream">
        <div className="abt-container abt-story">
          <div className="abt-story-media">
            <div className="abt-story-glow" />
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1000&q=80"
              alt="The AapkaPG team"
            />
          </div>
          <div className="abt-story-body">
            <span className="abt-eyebrow abt-eyebrow-light">Our Story</span>
            <h2 className="abt-h2">Born from a real problem</h2>
            <p className="abt-p">
              AapkaPG was founded on a simple but powerful observation: finding
              quality PG accommodation in India often involves endless phone calls,
              unreliable listings, hidden information and the anxious uncertainty of
              not knowing what you'll actually get when you arrive.
            </p>
            <p className="abt-p">
              For property owners, the challenges were equally real — managing
              inquiries, maintaining occupancy and reaching the right audience
              without the right tools. We saw an opportunity to bridge this gap. By
              combining technology with a deep understanding of India's rental
              accommodation market, we built a platform that empowers both sides of
              the equation.
            </p>
            <p className="abt-p">
              Today, AapkaPG serves as a trusted destination for students, working
              professionals and property owners looking for a smarter, more
              connected way to find their match.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="abt-section">
        <div className="abt-container">
          <div className="abt-section-head">
            <span className="abt-eyebrow abt-eyebrow-light">What we do</span>
            <h2 className="abt-h2">Two communities. One platform.</h2>
            <p className="abt-p abt-muted">
              A seamless experience built for both tenants and property owners.
            </p>
          </div>

          <div className="abt-do-grid">
            <div className="abt-do-card">
              <img
                src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1100&q=80"
                alt="For tenants"
              />
              <div className="abt-do-body">
                <div className="abt-do-tag">
                  <Users /> For Tenants
                </div>
                <h3 className="abt-h3">A home that actually feels like one</h3>
                <p className="abt-p">
                  Discover verified PG accommodations tailored to your requirements.
                  Search by location, filter by budget and browse detailed property
                  information — then connect directly with owners.
                </p>
              </div>
            </div>
            <div className="abt-do-card">
              <img
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1100&q=80"
                alt="For owners"
              />
              <div className="abt-do-body">
                <div className="abt-do-tag">
                  <Building2 /> For Property Owners
                </div>
                <h3 className="abt-h3">Reach more tenants, run a better business</h3>
                <p className="abt-p">
                  Reach more potential tenants and improve occupancy with ease. List
                  your property, boost visibility, generate quality leads and manage
                  enquiries — all from one platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tenant Features */}
      <section className="abt-section abt-cream">
        <div className="abt-container">
          <div className="abt-section-head">
            <span className="abt-eyebrow">For Tenants</span>
            <h2 className="abt-h2">Everything you need to find your next stay</h2>
            <p className="abt-p abt-muted">
              Tools built around the way modern renters actually search.
            </p>
          </div>
          <div className="abt-feat-grid">
            {tenantFeatures.map((f) => (
              <div key={f.title} className="abt-feat">
                <div className="abt-feat-icon">
                  <f.Icon />
                </div>
                <h3 className="abt-feat-title">{f.title}</h3>
                <p className="abt-feat-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Owner Features */}
      <section className="abt-section">
        <div className="abt-container">
          <div className="abt-section-head">
            <span className="abt-eyebrow abt-eyebrow-light">For Property Owners</span>
            <h2 className="abt-h2">Grow your rental business with confidence</h2>
            <p className="abt-p abt-muted">
              Powerful tools that bring the right tenants to your doorstep.
            </p>
          </div>
          <div className="abt-feat-grid">
            {ownerFeatures.map((f) => (
              <div key={f.title} className="abt-feat abt-feat-alt">
                <div className="abt-feat-icon">
                  <f.Icon />
                </div>
                <h3 className="abt-feat-title">{f.title}</h3>
                <p className="abt-feat-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="abt-section abt-cream">
        <div className="abt-container">
          <div className="abt-section-head">
            <span className="abt-eyebrow">What drives us</span>
            <h2 className="abt-h2">Our Core Values</h2>
            <p className="abt-p abt-muted">
              The principles that shape every product decision we make.
            </p>
          </div>
          <div className="abt-values-grid">
            {values.map((v) => (
              <div key={v.title} className="abt-value">
                <div className="abt-value-icon">
                  <v.Icon />
                </div>
                <h3 className="abt-feat-title">{v.title}</h3>
                <p className="abt-feat-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="abt-section">
        <div className="abt-container">
          <div className="abt-section-head">
            <span className="abt-eyebrow abt-eyebrow-light">Who we serve</span>
            <h2 className="abt-h2">Built for everyone in the shared-living ecosystem</h2>
            <p className="abt-p abt-muted">
              AapkaPG works for everyone who plays a role in India's PG and
              co-living market.
            </p>
          </div>
          <div className="abt-audience-grid">
            {audience.map((a) => (
              <div key={a.title} className="abt-audience">
                <div className="abt-audience-icon">
                  <a.Icon />
                </div>
                <h3 className="abt-feat-title">{a.title}</h3>
                <p className="abt-feat-desc">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey */}
      <section className="abt-section abt-cream">
        <div className="abt-container">
          <div className="abt-section-head">
            <span className="abt-eyebrow">Your journey on AapkaPG</span>
            <h2 className="abt-h2">Discover. Connect. Move in.</h2>
            <p className="abt-p abt-muted">
              From first search to first night — three simple steps.
            </p>
          </div>
          <div className="abt-journey">
            {journey.map((j) => (
              <div key={j.step} className="abt-journey-step">
                <div className="abt-journey-num">{j.step}</div>
                <div className="abt-journey-icon">
                  <j.Icon />
                </div>
                <h3 className="abt-feat-title">{j.title}</h3>
                <p className="abt-feat-desc">{j.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="abt-section">
        <div className="abt-container">
          <div className="abt-cta">
            <div className="abt-cta-glow abt-cta-glow-a" />
            <div className="abt-cta-glow abt-cta-glow-b" />
            <div className="abt-cta-inner">
              <Sparkles className="abt-cta-spark" />
              <p className="abt-cta-kicker">Building Trust, One Stay at a Time</p>
              <h2 className="abt-cta-title">
                Your search for the right stay starts here.
              </h2>
              <p className="abt-cta-sub">
                Whether you're a tenant looking for the perfect PG or an owner ready
                to grow your rental business — AapkaPG is your trusted partner.
              </p>
              <div className="abt-cta-actions">
                <Link to="/listings" className="abt-btn-primary">
                  Explore PGs <ArrowRight />
                </Link>
                <Link to="/contact" className="abt-btn-outline">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
